// src/generators/dzial2_algebra/temat2_rownania/podtemat3_wymierne_types/RationalTwoFracsQuadraticTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";
import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";

/* ========== helpers ========== */
const toL = (raw) => formatNumLatex(String(raw), "") || String(raw);
const coeffX = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${toL(a)}x`);
const withSign = (n) => (n >= 0 ? `+ ${toL(n)}` : `- ${toL(Math.abs(n))}`);
const isPerfectSquare = (n) => n >= 0 && Number.isInteger(Math.sqrt(n));

const gcd = (a, b) => {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { const t = a % b; a = b; b = t; }
  return a || 1;
};
const gcd3 = (A, B, C) => gcd(gcd(A, B), C);

function simplifyFrac(num, den) {
  if (den < 0) { num = -num; den = -den; }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

/** Uproszczony zapis punktu: całkowita lub ułamek MIESZANY (np. -7/6 → -1 1/6). */
function finalPointLatexMixed(num, den) {
  const { num: N0, den: D0 } = simplifyFrac(num, den);
  if (N0 % D0 === 0) return String(N0 / D0);
  const sign = N0 < 0 ? "-" : "";
  const N = Math.abs(N0), D = D0;
  const A = Math.floor(N / D), R = N % D;
  if (A > 0) return `${sign}${A}\\;\\dfrac{${R}}{${D}}`;
  return `${sign}\\dfrac{${R}}{${D}}`;
}

const fracLatex = (num, den) => `\\dfrac{${toL(num)}}{${toL(den)}}`;

/* ========== generator: dwie frakcje → kwadratowe po krzyżowym ========== */
export function generateTask() {
  const pick = (min, max) =>
    (Math.random() < 0.5 ? -1 : 1) *
    (Math.floor(Math.random() * (max - min + 1)) + min);

  while (true) {
    // mianowniki
    const c = pick(1, 4), g = pick(1, 4);
    const d = pick(1, 9), h = pick(1, 9);
    const x0 = simplifyFrac(-d, c), x1 = simplifyFrac(-h, g);
    if (x0.num * x1.den === x1.num * x0.den) continue; // różne punkty wykluczone

    // liczniki
    const a = pick(1, 6), e = pick(1, 6);
    if (a * g - e * c === 0) continue;
    const b = pick(1, 9), f = pick(1, 9);

    // współczynniki kwadratowego po krzyżowym
    const A2 = a * g - e * c;
    const B2 = a * h + b * g - e * d - f * c;
    const C2 = b * h - f * d;

    const Δ = B2 * B2 - 4 * A2 * C2;
    if (!isPerfectSquare(Math.abs(Δ)) || Δ < 0) continue; // „ładna” delta

    const latex =
      `\\dfrac{${coeffX(a)} ${withSign(b)}}{${coeffX(c)} ${withSign(d)}} = ` +
      `\\dfrac{${coeffX(e)} ${withSign(f)}}{${coeffX(g)} ${withSign(h)}}`;

    const s = Math.sqrt(Δ);
    const r1 = simplifyFrac(-B2 - s, 2 * A2);
    const r2 = simplifyFrac(-B2 + s, 2 * A2);

    return {
      type: "RationalTwoFracsQuadraticTask",
      data: {
        a, b, c, d, e, f, g, h, latex,
        x0_num: x0.num, x0_den: x0.den, x0_val: x0.num / x0.den,
        x1_num: x1.num, x1_den: x1.den, x1_val: x1.num / x1.den,
        A2, B2, C2, Δ,
        r1_num: r1.num, r1_den: r1.den, r1_val: r1.num / r1.den,
        r2_num: r2.num, r2_den: r2.den, r2_val: r2.num / r2.den,
      },
    };
  }
}

/* ========== treść zadania ========== */
export function renderTask({ data }) {
  return (
    <p>
      Podaj dziedzinę i rozwiąż równanie:{" "}
      <InlineMath math={data.latex} />
    </p>
  );
}

/* ========== input ========== */
export function renderInput(_, userInput = {}, onChange, disabled) {
  const count = userInput.count ?? "";
  const firstLabel = count === "1" ? "x_0 =" : "x_1 =";

  return (
    <div className="flex flex-col gap-4">
      {/* DZIEDZINA */}
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.7rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={"D:\\; x\\;\\in"} />
        </div>
        <IntervalInput
          value={userInput?.D || ""}
          onChange={(val) => onChange({ ...userInput, D: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. ℝ\{3}  lub  ℝ\{3, 5}  lub  ℝ - {3}"
        />
      </div>

      {/* FORMAT ODPOWIEDZI */}
      <div className="flex flex-col gap-2">
        <SelectInput
          options={[
            { value: "0", label: "brak rozwiązań" },
            { value: "1", label: "jedno rozwiązanie" },
            { value: "2", label: "dwa rozwiązania" },
          ]}
          value={count}
          onChange={(e) => {
            const v = e?.target?.value ?? e;
            const patch = { ...userInput, count: v };
            if (v === "0") { delete patch.x1; delete patch.x2; }
            else if (v === "1") { delete patch.x2; }
            onChange(patch);
          }}
          placeholder="Wybierz liczbę rozwiązań"
          disabled={disabled}
        />

        {(count === "1" || count === "2") && (
          <div className="flex items-center gap-2">
            <span><InlineMath math={firstLabel} /></span>
            <FractionInput
              value={userInput.x1 || ""}
              onChange={(val) => onChange({ ...userInput, x1: val })}
              disabled={disabled}
              label={count === "1" ? "x₀" : "x₁"}
            />
          </div>
        )}

        {count === "2" && (
          <div className="flex items-center gap-2">
            <span><InlineMath math={"x_2 ="} /></span>
            <FractionInput
              value={userInput.x2 || ""}
              onChange={(val) => onChange({ ...userInput, x2: val })}
              disabled={disabled}
              label="x₂"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* ========== parser ℝ\{a,b} (dla sprawdzania) ========== */
function parseComplementSet(raw) {
  if (!raw) return null;
  // akceptuj: R - {…}, R − {…}, R – {…}, R \ {…}, R / {…}
  const m = String(raw).match(/[Rℝ]\s*(?:[-−–\/\\])\s*{([^}]*)}/i);
  if (!m) return null;

  const items = m[1]
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (!items.length) return null;

  const vals = items
    .map((s) => parseRootFractionInput(s.replace(",", ".")))
    .filter((v) => Number.isFinite(v));

  return vals.length ? vals : null;
}

/* ========== sprawdzanie odpowiedzi ========== */
export function checkAnswer({ data }, userInput = {}) {
  const tol = 0.01 + 1e-9;
  const eq = (x, y) => Math.abs(x - y) <= tol;

  const x0 = data.x0_val, x1 = data.x1_val;
  const [p, q] = x0 < x1 ? [x0, x1] : [x1, x0];

  // --- DZIEDZINA ---
  const rawD = typeof userInput?.D === "string" ? userInput.D : (userInput?.D?.raw || "");
  let domainOK = false;

  // (A) ℝ\{a,b}
  const setVals = parseComplementSet(rawD);
  if (setVals && setVals.length >= 2) {
    const got = setVals.sort((u, v) => u - v);
    domainOK = eq(got[0], p) && eq(got[1], q);
  }

  // (B) suma przedziałów
  if (!domainOK) {
    const parsed = parseIntervalInput(rawD);
    if (parsed && parsed.kind === "intervals") {
      const ints = parsed.intervals;

      // trzy przedziały: (-∞, p) ∪ (p, q) ∪ (q, ∞)
      if (ints.length === 3) {
        const ends = [];
        for (const I of ints) {
          if (Number.isFinite(I.left))  ends.push({ v: I.left, open: !I.leftClosed });
          if (Number.isFinite(I.right)) ends.push({ v: I.right, open: !I.rightClosed });
        }
        const uniq = [];
        for (const e of ends) if (!uniq.some(u => eq(u.v, e.v))) uniq.push(e);
        if (uniq.length === 2 && uniq.every(u => u.open)) {
          const vs = uniq.map(u => u.v).sort((u, v) => u - v);
          domainOK = eq(vs[0], p) && eq(vs[1], q);
        }
      }

      // dwa przedziały: (-∞, p) ∪ (q, ∞)
      if (!domainOK && ints.length === 2) {
        const [I, J] = ints;
        const case1 =
          I.left === -Infinity && !I.rightClosed && Number.isFinite(I.right) &&
          J.right === Infinity && !J.leftClosed && Number.isFinite(J.left) &&
          eq(I.right, p) && eq(J.left, q);
        const case2 =
          J.left === -Infinity && !J.rightClosed && Number.isFinite(J.right) &&
          I.right === Infinity && !I.leftClosed && Number.isFinite(I.left) &&
          eq(J.right, p) && eq(I.left, q);
        domainOK = case1 || case2;
      }
    }
  }

  // --- OCZEKIWANE ROZWIĄZANIA (po filtrze dziedziny) ---
  const candRaw = [data.r1_val, data.r2_val];
  const cand = Math.abs(candRaw[0] - candRaw[1]) <= 1e-9 ? [candRaw[0]] : candRaw;
  const expected = cand
    .filter(v => Math.abs(v - x0) > 1e-9 && Math.abs(v - x1) > 1e-9)
    .sort((u, v) => u - v);
  const expectedCount = expected.length;

  // --- Odpowiedź użytkownika ---
  const picked = userInput.count; // '0' | '1' | '2'
  let solutionOK = false;

  if (picked === String(expectedCount)) {
    if (expectedCount === 0) {
      solutionOK = true;
    } else if (expectedCount === 1) {
      const u1 = userInput.x1?.parsed;
      solutionOK = Number.isFinite(u1) && Math.abs(u1 - expected[0]) <= tol;
    } else {
      const u1 = userInput.x1?.parsed;
      const u2 = userInput.x2?.parsed;
      if (Number.isFinite(u1) && Number.isFinite(u2)) {
        solutionOK =
          (Math.abs(u1 - expected[0]) <= tol && Math.abs(u2 - expected[1]) <= tol) ||
          (Math.abs(u1 - expected[1]) <= tol && Math.abs(u2 - expected[0]) <= tol);
      }
    }
  }

  return { correct: domainOK && solutionOK, expected: { D: [p, q], X: expected } };
}

/* ========== poprawna odpowiedź (KRÓTKA) ========== */
export function renderAnswer({ data }) {
  // uporządkuj i uprość punkty wykluczone
  const exc = [
    { v: data.x0_num / data.x0_den, L: finalPointLatexMixed(data.x0_num, data.x0_den) },
    { v: data.x1_num / data.x1_den, L: finalPointLatexMixed(data.x1_num, data.x1_den) },
  ].sort((u, v) => u.v - v.v);

  // rozwiązania po filtrze dziedziny
  const tol = 1e-9;
  const isExcluded = (v) => Math.abs(v - exc[0].v) <= tol || Math.abs(v - exc[1].v) <= tol;

  const sols = [];
  const r1 = data.r1_num / data.r1_den;
  const r2 = data.r2_num / data.r2_den;
  if (!isExcluded(r1)) sols.push(finalPointLatexMixed(data.r1_num, data.r1_den));
  if (Math.abs(r2 - r1) > tol && !isExcluded(r2)) sols.push(finalPointLatexMixed(data.r2_num, data.r2_den));

  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div>
        <InlineMath math={`D:\\; x\\in\\mathbb{R}\\setminus\\{${exc[0].L},\\,${exc[1].L}\\}`} />
      </div>
      <div style={{ marginTop: "0.7rem" }}>
        {sols.length
          ? <InlineMath math={`x\\in\\{${sols.join(",\\,")}\\}`} />
          : <i>Brak rozwiązań (wynik wypada z dziedziny).</i>}
      </div>
    </div>
  );
}

/* ========== Twoja odpowiedź (podgląd) ========== */
export function renderUserAnswer(userInput = {}) {
  const rawD = typeof userInput === "string"
    ? userInput
    : (userInput?.D?.raw || userInput?.D || "");
  const dLatex = intervalToLatex(rawD, { withX: true });

  const count = userInput.count;
  const x1Prev = userInput.x1?.raw
    ? formatFracPreview(userInput.x1.raw, "x_1")
    : null;
  const x2Prev = userInput.x2?.raw
    ? formatFracPreview(userInput.x2.raw, "x_2")
    : null;

  const lineStyle = { marginTop: "0.6rem", lineHeight: 1.3 };

  return (
    <div style={{ marginTop: "0.45rem" }}>
      {rawD ? (
        <div>
          <InlineMath math={`\\displaystyle D:\\; ${dLatex}`} />
        </div>
      ) : null}

      {count === "0" && <div style={lineStyle}><i>Brak rozwiązań</i></div>}

      {count === "1" && (
        <div style={lineStyle}>
          {x1Prev ? <InlineMath math={x1Prev} /> : <i>(brak x₀)</i>}
        </div>
      )}

      {count === "2" && (
        <>
          <div style={lineStyle}>
            {x1Prev ? <InlineMath math={x1Prev} /> : <i>(brak x₁)</i>}
          </div>
          <div style={lineStyle}>
            {x2Prev ? <InlineMath math={x2Prev} /> : <i>(brak x₂)</i>}
          </div>
        </>
      )}

      {!rawD && !count ? <i>(brak odpowiedzi)</i> : null}
    </div>
  );
}

/* ========== Wyjaśnienie (pełne) ========== */
export function renderExplanation({ data }) {
  const {
    a, b, c, d, e, f, g, h,
    A2, B2, C2, Δ,
    r1_num, r1_den, r2_num, r2_den
  } = data;

  // pomocnicze formatowanie (korzysta z istniejących w pliku helperów)
  const toL = (v) => formatNumLatex(String(v), "") || String(v);
  const coeffX = (k) => (k === 1 ? "x" : k === -1 ? "-x" : `${toL(k)}x`);
  const withSign = (n) => (n >= 0 ? `+ ${toL(n)}` : `- ${toL(Math.abs(n))}`);

  // wykluczenia z dziedziny — uproszczone i posortowane
  const excl = [
    { v: -d / c, L: finalPointLatexMixed(-d, c) },
    { v: -h / g, L: finalPointLatexMixed(-h, g) },
  ].sort((u, v) => u.v - v.v);
  const exc1 = excl[0].L, exc2 = excl[1].L;

  // składanie wielomianów bez "1" przy x, x^2
  const firstTerm = (coef, sym) => {
    if (coef === 0) return null;
    const abs = Math.abs(coef);
    const body = sym ? (abs === 1 ? sym : `${toL(abs)}${sym}`) : toL(abs);
    return coef < 0 ? `-${body}` : body;
  };
  const nextTerm = (coef, sym) => {
    if (coef === 0) return null;
    const abs = Math.abs(coef);
    const body = sym ? (abs === 1 ? sym : `${toL(abs)}${sym}`) : toL(abs);
    return `${coef < 0 ? "-" : "+"}\\;${body}`;
  };
  const poly = (A, B, C) => {
    const out = [];
    const t1 = firstTerm(A, "x^2"); if (t1) out.push(t1);
    const t2 = nextTerm(B, "x");    if (t2) out.push(t2);
    const t3 = nextTerm(C, "");     if (t3) out.push(t3);
    return out.length ? out.join("\\;") : "0";
  };

  // po krzyżowym
  const Lxx = a * g,  Lx = a * h + b * g,  Lc = b * h;
  const Rxx = c * e,  Rx = c * f + d * e,  Rc = d * f;

  // opcjonalne skrócenie równania kwadratowego (NWD i znak, by A>0)
  const gcd = (x, y) => { x = Math.abs(x); y = Math.abs(y); while (y) { const t = x % y; x = y; y = t; } return x || 1; };
  const k = (A2 < 0 ? -1 : 1) * gcd(gcd(A2, B2), C2);
  const shortened = k !== 1;
  const A = shortened ? A2 / k : A2;
  const B = shortened ? B2 / k : B2;
  const C = shortened ? C2 / k : C2;

  /* ---------- KROK 3: Δ, √Δ oraz x1/x2 z podstawieniem; skrócenie pośrednie warunkowe; wynik końcowy zawsze ---------- */
  const par  = (n) => (n < 0 ? `(${toL(n)})` : toL(n));
  const frac = (num, den) => `\\dfrac{${toL(num)}}{${toL(den)}}`;
  const same = (x, y) => Math.abs(x - y) <= 1e-9;

  const b2num  = B2 * B2;
  const fourac = 4 * A2 * C2;
  const s      = Math.sqrt(Math.abs(Δ));

  // podstawienia do (-b ± √Δ)/(2a) – używamy A2, B2 (przed ewentualnym skróceniem całego równania)
  const num1Sub = `-(${toL(B2)}) - ${toL(s)}`;
  const num2Sub = `-(${toL(B2)}) + ${toL(s)}`;
  const denSub  = `2\\cdot ${par(A2)}`;

  // „surowe” licznik/mianownik
  const N1 = -B2 - s, N2 = -B2 + s, Dn = 2 * A2;

  // RAW
  const raw1 = frac(N1, Dn);
  const raw2 = frac(N2, Dn);

  // REDUKCJA (warunkowa, tylko gdy coś się zmienia)
  const sF1 = simplifyFrac(N1, Dn);
  const sF2 = simplifyFrac(N2, Dn);
  const red1 = frac(sF1.num, sF1.den);
  const red2 = frac(sF2.num, sF2.den);
  const vRaw1 = N1 / Dn, vRed1 = sF1.num / sF1.den;
  const vRaw2 = N2 / Dn, vRed2 = sF2.num / sF2.den;
  const showRed1 = !same(vRaw1, vRed1);
  const showRed2 = !same(vRaw2, vRed2);

  // OSTATNI ZAPIS (mieszany / całkowity) – ZAWSZE pokazujemy jako ostatnią część łańcucha
  const fin1 = finalPointLatexMixed(r1_num, r1_den);
  const fin2 = finalPointLatexMixed(r2_num, r2_den);

  // rozwiązania do puenty (po filtrze dziedziny)
  const tol = 1e-9;
  const outside = (v) => Math.abs(v - excl[0].v) <= tol || Math.abs(v - excl[1].v) <= tol;
  const sols = [];
  const r1v = r1_num / r1_den, r2v = r2_num / r2_den;
  if (!outside(r1v)) sols.push(finalPointLatexMixed(r1_num, r1_den));
  if (Math.abs(r2v - r1v) > tol && !outside(r2v)) sols.push(finalPointLatexMixed(r2_num, r2_den));

  const Dline = `D:\\; x \\in \\mathbb{R}\\setminus\\{ ${exc1},\\, ${exc2} \\}`;
  const Sline = sols.length ? `x\\in\\{ ${sols.join(",\\,")} \\}` : `\\varnothing`;

  return (
    <div className="space-y-4">
      {/* Krok 1 — dziedzina */}
      <p><strong>Krok 1:</strong> Warunki z mianowników i wykluczenie punktów.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          `\\begin{aligned}
            ${coeffX(c)}\\;${withSign(d)} &= 0 \\;&\\Rightarrow&\\ x = ${exc1} \\\\
            ${coeffX(g)}\\;${withSign(h)} &= 0 \\;&\\Rightarrow&\\ x = ${exc2}
          \\end{aligned}`
        } />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x \\neq ${exc1}\\qquad x \\neq ${exc2}`} />
      </div>

      {/* Krok 2 — krzyżowe, porządkowanie, (opcjonalnie) skrócenie */}
      <p><strong>Krok 2:</strong> Wymnażamy na krzyż i porządkujemy do postaci kwadratowej.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          `\\dfrac{${coeffX(a)}\\;${withSign(b)}}{${coeffX(c)}\\;${withSign(d)}} =
           \\dfrac{${coeffX(e)}\\;${withSign(f)}}{${coeffX(g)}\\;${withSign(h)}}`
        } />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`(${coeffX(a)}\\;${withSign(b)})(${coeffX(g)}\\;${withSign(h)}) = (${coeffX(c)}\\;${withSign(d)})(${coeffX(e)}\\;${withSign(f)})`} />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`${poly(Lxx, Lx, Lc)} = ${poly(Rxx, Rx, Rc)}`} />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`${poly(A2, B2, C2)} = 0${shortened ? `\\;\\;|\\;:\\;${toL(k)}` : ""}`} />
      </div>
      {shortened && (
        <div style={{ textAlign: "center" }}>
          <BlockMath math={`${poly(A, B, C)} = 0`} />
        </div>
      )}

      {/* Krok 3 — Δ, √Δ oraz x1/x2 (dwie kolumny; końcowy wynik zawsze) */}
      <p><strong>Krok 3:</strong> Liczymy deltę i pierwiastki.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          `\\Delta = ${B2 < 0 ? `(${toL(B2)})` : toL(B2)}^{2} - 4\\cdot ${par(A2)}\\cdot ${par(C2)} = ${toL(b2num)} - ${toL(fourac)} = ${toL(Δ)}`
        } />
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`\\sqrt{\\Delta} = \\sqrt{${toL(Δ)}} = ${toL(s)}`} />
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: "2.5rem", flexWrap: "wrap" }}>
        <BlockMath math={
          `x_1 = \\dfrac{${num1Sub}}{${denSub}} = ${raw1}`
          + (showRed1 ? ` = ${red1}` : "")
          + ` = ${fin1}`
        } />
        <BlockMath math={
          `x_2 = \\dfrac{${num2Sub}}{${denSub}} = ${raw2}`
          + (showRed2 ? ` = ${red2}` : "")
          + ` = ${fin2}`
        } />
      </div>

      {/* Krok 4 — puenta */}
      <p><strong>Krok 4:</strong> Sprawdzamy dziedzinę i zbieramy wynik.</p>
      <div style={{ textAlign: "center", marginBottom: "0.25rem" }}>
        <BlockMath math={Dline} />
      </div>
      <div style={{ textAlign: "center", marginTop: "-2px" }}>
        <BlockMath math={Sline} />
      </div>
    </div>
  );
}



